import type { ExamForm, LearnerExam } from "../content-schema";

export type ExamArchiveSort = "quality" | "newest" | "oldest" | "title" | "coverage" | "questions" | "easiest" | "hardest";
export type ExamArchiveReadiness = "all" | "ready" | "playable" | "browse";
export type ExamArchiveCoverage = "all" | "full_sat" | "complete" | "partial";
export type ExamArchiveIdentity = "all" | ExamForm["identityStatus"];

export interface ExamArchiveFilters {
  query: string;
  sort: ExamArchiveSort;
  readiness: ExamArchiveReadiness;
  coverage: ExamArchiveCoverage;
  identity: ExamArchiveIdentity;
  year: string;
  region: string;
}

export interface ExamArchiveItem {
  id: string;
  title: string;
  sitting: string | null;
  year: string;
  region: string;
  regionCategory: string;
  formLabel: string | null;
  assessmentModel: ExamForm["assessmentModel"];
  identityStatus: ExamForm["identityStatus"];
  eligibility: ExamForm["eligibility"];
  moduleCount: number;
  completeModules: number;
  alternateModuleCount: number;
  representedQuestions: number;
  expectedQuestions: number;
  sourceCount: number;
  coverageRatio: number;
  isComplete: boolean;
  isFullSat: boolean;
  searchText: string;
  difficulty?: ExamDifficultySummary;
}

/** Existing editorial bands, aggregated over the represented fixed route. */
export interface ExamDifficultySummary {
  medianBand: number | null;
  classified: number;
  represented: number;
}

export function summarizeExamDifficulty(bands: Array<number | null | undefined>): ExamDifficultySummary {
  const classified = bands.filter((band): band is number => typeof band === "number" && band >= 1 && band <= 7).sort((a, b) => a - b);
  const midpoint = Math.floor(classified.length / 2);
  return {
    medianBand: classified.length === 0 ? null : classified.length % 2 ? classified[midpoint] : (classified[midpoint - 1] + classified[midpoint]) / 2,
    classified: classified.length,
    represented: bands.length
  };
}

export function examFormCompletenessLabel(item: ExamArchiveItem) {
  return item.isFullSat
    ? "Full SAT · 98/98"
    : `Not full SAT · ${item.representedQuestions}/${item.expectedQuestions} q · ${item.completeModules}/${item.moduleCount} modules`;
}

export const defaultExamArchiveFilters: ExamArchiveFilters = {
  query: "",
  sort: "quality",
  readiness: "all",
  coverage: "all",
  identity: "all",
  year: "all",
  region: "all"
};

export const examArchiveCookieKey = "sat_archive_exam_filters";
export const examArchiveFilterKeys = Object.keys(defaultExamArchiveFilters) as Array<keyof ExamArchiveFilters>;

const allowedSorts = new Set<ExamArchiveSort>(["quality", "newest", "oldest", "title", "coverage", "questions", "easiest", "hardest"]);
const allowedReadiness = new Set<ExamArchiveReadiness>(["all", "ready", "playable", "browse"]);
const allowedCoverage = new Set<ExamArchiveCoverage>(["all", "full_sat", "complete", "partial"]);
const allowedIdentity = new Set<ExamArchiveIdentity>(["all", "verified", "probable", "conflicting", "unresolved"]);

export function normalizeExamArchiveFilters(
  input: Partial<Record<keyof ExamArchiveFilters, unknown>>,
  items: ExamArchiveItem[]
): ExamArchiveFilters {
  const years = new Set(items.map((item) => item.year));
  const regions = new Set(items.map((item) => item.regionCategory));
  const stringValue = (key: keyof ExamArchiveFilters) => typeof input[key] === "string" ? input[key] : undefined;
  const query = stringValue("query")?.slice(0, 160) ?? defaultExamArchiveFilters.query;
  const sort = stringValue("sort") as ExamArchiveSort | undefined;
  const readiness = stringValue("readiness") as ExamArchiveReadiness | undefined;
  const coverage = stringValue("coverage") as ExamArchiveCoverage | undefined;
  const identity = stringValue("identity") as ExamArchiveIdentity | undefined;
  const year = stringValue("year");
  const region = stringValue("region");

  return {
    query,
    sort: sort && allowedSorts.has(sort) ? sort : defaultExamArchiveFilters.sort,
    readiness: readiness && allowedReadiness.has(readiness) ? readiness : defaultExamArchiveFilters.readiness,
    coverage: coverage && allowedCoverage.has(coverage) ? coverage : defaultExamArchiveFilters.coverage,
    identity: identity && allowedIdentity.has(identity) ? identity : defaultExamArchiveFilters.identity,
    year: year === "all" || (year && years.has(year)) ? year : defaultExamArchiveFilters.year,
    region: region === "all" || (region && regions.has(region)) ? region : defaultExamArchiveFilters.region
  };
}

export function examRegionCategory(region: string | null) {
  const value = region?.trim().toLocaleLowerCase() ?? "";
  if (/asia[\s-]+pacific/.test(value)) return "Asia-Pacific";
  if (value.includes("new zealand")) return "New Zealand";
  if (value.includes("north america") || value.includes("united states") || value === "us") return "North America";
  if (value.includes("international")) return "International";
  return "Unknown";
}

export function examArchiveItem(exam: ExamForm | LearnerExam): ExamArchiveItem {
  const primaryModules = exam.modules.filter((module) => !module.variantOf);
  const alternateModuleCount = exam.modules.length - primaryModules.length;
  const representedQuestions = primaryModules.reduce(
    (sum, module) => sum + module.items.filter((item) => Boolean(item.questionId)).length,
    0
  );
  const expectedQuestions = primaryModules.reduce((sum, module) => sum + module.expectedCount, 0);
  const completeModules = primaryModules.filter((module) =>
    module.complete && module.items.filter((item) => Boolean(item.questionId)).length === module.expectedCount
  ).length;
  const isComplete = primaryModules.length > 0
    && completeModules === primaryModules.length
    && representedQuestions === expectedQuestions;
  const sections = new Set(primaryModules.map((module) => module.section));
  const isFullSat = isComplete
    && expectedQuestions === 98
    && sections.has("reading_writing")
    && sections.has("math");
  const region = exam.region?.trim() || "Region unresolved";

  return {
    id: exam.id,
    title: exam.title.replaceAll("—", "-").replaceAll("–", "-"),
    sitting: exam.sitting,
    year: exam.sitting?.slice(0, 4) ?? "Unknown",
    region,
    regionCategory: examRegionCategory(exam.region),
    formLabel: exam.formLabel,
    assessmentModel: exam.assessmentModel,
    identityStatus: exam.identityStatus,
    eligibility: exam.eligibility,
    moduleCount: primaryModules.length,
    completeModules,
    alternateModuleCount,
    representedQuestions,
    expectedQuestions,
    sourceCount: "sourceLinks" in exam ? exam.sourceLinks.length : 0,
    coverageRatio: expectedQuestions > 0 ? representedQuestions / expectedQuestions : 0,
    isComplete,
    isFullSat,
    searchText: [
      exam.id,
      exam.title,
      exam.sitting,
      region,
      exam.formLabel,
      exam.eligibility,
      exam.identityStatus
    ].filter(Boolean).join(" ").toLocaleLowerCase()
  };
}

const readinessRank: Record<ExamForm["eligibility"], number> = {
  fixed_ready: 4,
  adaptive_ready: 4,
  partial_playable: 2,
  browse_only: 1
};

const identityRank: Record<ExamForm["identityStatus"], number> = {
  verified: 4,
  probable: 3,
  unresolved: 2,
  conflicting: 1
};

function compareQuality(left: ExamArchiveItem, right: ExamArchiveItem) {
  return readinessRank[right.eligibility] - readinessRank[left.eligibility]
    || Number(right.isFullSat) - Number(left.isFullSat)
    || right.coverageRatio - left.coverageRatio
    || identityRank[right.identityStatus] - identityRank[left.identityStatus]
    || right.completeModules - left.completeModules
    || (right.sitting ?? "").localeCompare(left.sitting ?? "");
}

export function filterAndSortExams<T extends ExamArchiveItem>(items: T[], filters: ExamArchiveFilters): T[] {
  const query = filters.query.trim().toLocaleLowerCase();
  return items.filter((item) => {
    if (query && !item.searchText.includes(query)) return false;
    if (filters.readiness === "ready" && !["fixed_ready", "adaptive_ready"].includes(item.eligibility)) return false;
    if (filters.readiness === "playable" && item.eligibility !== "partial_playable") return false;
    if (filters.readiness === "browse" && item.eligibility !== "browse_only") return false;
    if (filters.coverage === "full_sat" && !item.isFullSat) return false;
    if (filters.coverage === "complete" && !item.isComplete) return false;
    if (filters.coverage === "partial" && item.isFullSat) return false;
    if (filters.identity !== "all" && item.identityStatus !== filters.identity) return false;
    if (filters.year !== "all" && item.year !== filters.year) return false;
    if (filters.region !== "all" && item.regionCategory !== filters.region) return false;
    return true;
  }).sort((left, right) => {
    const title = left.title.localeCompare(right.title);
    if (filters.sort === "easiest" || filters.sort === "hardest") {
      const a = left.difficulty?.medianBand ?? null;
      const b = right.difficulty?.medianBand ?? null;
      if (a === null || b === null) return Number(a === null) - Number(b === null) || compareQuality(left, right) || title;
      return (filters.sort === "easiest" ? a - b : b - a) || compareQuality(left, right) || title;
    }
    if (filters.sort === "newest") return (right.sitting ?? "").localeCompare(left.sitting ?? "") || title;
    if (filters.sort === "oldest") return (left.sitting ?? "9999").localeCompare(right.sitting ?? "9999") || title;
    if (filters.sort === "title") return title;
    if (filters.sort === "coverage") return right.coverageRatio - left.coverageRatio || compareQuality(left, right) || title;
    if (filters.sort === "questions") return right.representedQuestions - left.representedQuestions || compareQuality(left, right) || title;
    return compareQuality(left, right) || title;
  });
}
